import React from 'react';

// Base skeleton component with shimmer effect
export const Skeleton: React.FC<{
  className?: string;
  children?: React.ReactNode;
}> = ({ className = '', children }) => (
  <div
    className={`bg-gradient-to-r from-teddy-100 via-teddy-200 to-teddy-100 animate-shimmer bg-[length:200%_100%] rounded ${className}`}
  >
    {children}
  </div>
);

// Skeleton for treasure cards (matches TreasureCard layout)
export const TreasureCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-card">
    {/* Image skeleton with 4:3 aspect ratio */}
    <Skeleton className="aspect-[4/3]" />
    
    {/* Content skeleton */}
    <div className="p-4 space-y-3">
      {/* Location skeleton */}
      <Skeleton className="h-4 w-2/3" />
      
      {/* Categories/Tags skeleton */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <Skeleton className="h-6 w-16 rounded-lg" />
          <Skeleton className="h-6 w-20 rounded-lg" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="h-6 w-14 rounded-xl" />
          <Skeleton className="h-6 w-18 rounded-xl" />
          <Skeleton className="h-6 w-12 rounded-xl" />
        </div>
      </div>
      
      {/* Meta info skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton className="h-4 w-20" />
        <div className="flex items-center gap-4">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-8" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for list view items (matches MapListView layout)
export const ListItemSkeleton: React.FC = () => (
  <div className="p-4 border-b border-urban/10">
    <div className="flex gap-3">
      {/* Image skeleton */}
      <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
      
      {/* Content skeleton */}
      <div className="flex-1 space-y-2">
        {/* Title/Location skeleton */}
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        
        {/* Tags skeleton */}
        <div className="flex gap-1">
          <Skeleton className="h-5 w-12 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
        
        {/* Meta skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-8" />
        </div>
      </div>
    </div>
  </div>
);

// Skeleton for search results
export const SearchResultSkeleton: React.FC = () => (
  <div className="space-y-4">
    {/* Search header skeleton */}
    <div className="flex justify-between items-center">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-4 w-24" />
    </div>
    
    {/* Results skeleton */}
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex gap-3 p-3 bg-white rounded-lg">
          <Skeleton className="w-12 h-12 rounded" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton for form fields
export const FormFieldSkeleton: React.FC<{ 
  label?: boolean;
  height?: 'sm' | 'md' | 'lg';
}> = ({ label = true, height = 'md' }) => {
  const heightClasses = {
    sm: 'h-8',
    md: 'h-12',
    lg: 'h-32'
  };

  return (
    <div className="space-y-2">
      {label && <Skeleton className="h-4 w-24" />}
      <Skeleton className={`w-full rounded-lg ${heightClasses[height]}`} />
    </div>
  );
};

// Skeleton for profile/user info
export const ProfileSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg p-6 shadow-card">
    <div className="flex items-center gap-4 mb-6">
      <Skeleton className="w-16 h-16 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
    
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div className="text-center">
        <Skeleton className="h-8 w-12 mx-auto mb-2" />
        <Skeleton className="h-4 w-16 mx-auto" />
      </div>
      <div className="text-center">
        <Skeleton className="h-8 w-12 mx-auto mb-2" />
        <Skeleton className="h-4 w-16 mx-auto" />
      </div>
      <div className="text-center">
        <Skeleton className="h-8 w-12 mx-auto mb-2" />
        <Skeleton className="h-4 w-16 mx-auto" />
      </div>
    </div>
    
    <div className="space-y-3">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </div>
);

// Skeleton for admin dashboard stats
export const StatCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg p-6 shadow-card">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-6 w-24" />
      <Skeleton className="w-8 h-8 rounded" />
    </div>
    <Skeleton className="h-8 w-16 mb-2" />
    <Skeleton className="h-4 w-20" />
  </div>
);

// Skeleton for map markers/popups
export const MapPopupSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg p-4 shadow-lg max-w-64">
    <Skeleton className="w-full h-24 rounded mb-3" />
    <Skeleton className="h-5 w-3/4 mb-2" />
    <Skeleton className="h-4 w-1/2 mb-3" />
    <Skeleton className="h-8 w-full rounded" />
  </div>
);

// Grid of skeleton cards
export const SkeletonGrid: React.FC<{
  count?: number;
  type?: 'card' | 'list';
}> = ({ count = 8, type = 'card' }) => {
  const SkeletonComponent = type === 'card' ? TreasureCardSkeleton : ListItemSkeleton;
  
  if (type === 'list') {
    return (
      <div className="bg-white rounded-lg shadow-card overflow-hidden">
        {Array.from({ length: count }).map((_, index) => (
          <SkeletonComponent key={`skeleton-${index}`} />
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonComponent key={`skeleton-${index}`} />
      ))}
    </div>
  );
};